import { useCallback } from 'react';
import { toast } from 'sonner';

import { useProviderServiceDestinationsUpdateMutation } from '@/services/api/generated/providerDestinationsService';
import type {
  CreateTreatmentRecordRequestSchema,
  TreatmentRecordTypes,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import type { DestinationOption } from '@/types/provider-destinations';
import {
  DestinationChannelType,
  LEGACY_SCOPE_MAP,
  SaveMode,
} from '@/types/provider-destinations';

import { groupSelectedScopesByDestination } from './threading';
import type { WizardScopeSelection } from './useCreateRequestWizard';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map a DestinationChannelType to the channel type expected by the
 * create-request API.
 */
function mapChannelType(
  channelType: DestinationChannelType,
): 'fax' | 'email' | 'mail' | 'portal' {
  switch (channelType) {
    case DestinationChannelType.FAX:
      return 'fax';
    case DestinationChannelType.EMAIL:
      return 'email';
    case DestinationChannelType.MAIL:
      return 'mail';
    case DestinationChannelType.PORTAL:
      return 'portal';
    default:
      return 'fax';
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseWizardSubmitOpts {
  selectedScopes: WizardScopeSelection[];
  getProviderServiceIdForScope: (
    scope: WizardScopeSelection['scope'],
  ) => string | null;
  matterId: string;
  providerId: string;
  notes: string;
  serviceStartDate: string;
  serviceEndDate: string;
  onSuccess: () => void;
  setSubmitting: (v: boolean) => void;
}

export const useWizardSubmit = ({
  selectedScopes,
  getProviderServiceIdForScope,
  matterId,
  providerId,
  notes,
  serviceStartDate,
  serviceEndDate,
  onSuccess,
  setSubmitting,
}: UseWizardSubmitOpts) => {
  const [setDestination] = useProviderServiceDestinationsUpdateMutation();
  const [createRequest] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsCreateMutation();

  const submit = useCallback(async () => {
    setSubmitting(true);

    try {
      // 1. Save destinations where save behavior is SAVE_FOR_FUTURE
      const scopesReadyForCreation = selectedScopes.filter(
        (s) => s.destination !== null || s.createWithoutDestination,
      );
      const scopesWithDest = scopesReadyForCreation.filter(
        (s): s is WizardScopeSelection & { destination: DestinationOption } =>
          s.destination !== null,
      );

      const savePromises = scopesWithDest
        .filter((s) => s.saveBehavior === SaveMode.SAVE_FOR_FUTURE)
        .filter((s) => Boolean(s.destination.contact_point_id))
        .map((s) => {
          const providerServiceId = getProviderServiceIdForScope(s.scope);
          if (!providerServiceId) return null;

          return setDestination({
            providerServiceId,
            patchedProviderServiceDestinationsPatchRequest: {
              scope: s.scope,
              contact_point_id: s.destination.contact_point_id,
              save_mode: s.saveBehavior,
            },
          }).unwrap();
        })
        .filter(Boolean);
      await Promise.all(savePromises);

      // 2. Thread selected scopes by destination/contact method and create
      // one request per thread group.
      const threadGroups =
        groupSelectedScopesByDestination(scopesReadyForCreation);
      const createPromises = threadGroups.map((group) => {
        const providedRecords = group.scopes
          .map((scopeSelection) =>
            LEGACY_SCOPE_MAP.toLegacy(scopeSelection.scope),
          )
          .filter(Boolean) as TreatmentRecordTypes[];

        const requestPayload: CreateTreatmentRecordRequestSchema & {
          service_start_date?: string;
          service_end_date?: string;
        } = {
          actor: 'user',
          is_certified: false,
          is_urgent: false,
          status: 'pending',
          provided_records: providedRecords,
          ...(notes.trim() ? { metadata: { notes: notes.trim() } } : {}),
          ...(serviceStartDate ? { service_start_date: serviceStartDate } : {}),
          ...(serviceEndDate ? { service_end_date: serviceEndDate } : {}),
        };

        if (group.destination) {
          const channelType = mapChannelType(group.destination.channel_type);
          const channelPayload: Record<string, unknown> = {
            type: channelType,
          };

          // Set the value field based on channel type
          switch (channelType) {
            case 'fax':
              channelPayload.fax = group.destination.value;
              break;
            case 'email':
              channelPayload.email = group.destination.value;
              break;
            case 'mail':
              channelPayload.address = group.destination.value;
              break;
            case 'portal':
              channelPayload.instructions = group.destination.value;
              break;
            default:
              channelPayload.fax = group.destination.value;
              break;
          }

          // Add follow-up phone if provided and channel supports it
          if (
            group.followUpPhone &&
            (channelType === 'fax' || channelType === 'email')
          ) {
            channelPayload.phone_number = group.followUpPhone;
          }

          if (group.destination.contact_point_id) {
            channelPayload.provider_directory_contact_point_id =
              group.destination.contact_point_id;
          }

          requestPayload.channel = channelPayload as never;
        }

        return createRequest({
          matterId,
          providerId,
          createTreatmentRecordRequestSchema: requestPayload,
        }).unwrap();
      });

      await Promise.all(createPromises);

      const count = createPromises.length;
      toast.success(
        `${count} request${count !== 1 ? 's' : ''} created successfully`,
      );
      onSuccess();
    } catch (error: unknown) {
      const data = (error as { data?: unknown })?.data;
      let message: string;
      if (Array.isArray(data)) {
        message = data
          .map((item) =>
            typeof item === 'string'
              ? item
              : item?.msg || item?.detail || JSON.stringify(item),
          )
          .join('; ');
      } else {
        message =
          (data as { detail?: string })?.detail ||
          (data as { message?: string })?.message ||
          (error as { message?: string })?.message ||
          'An unexpected error occurred';
      }

      if (message.includes('workflow is not enabled')) {
        toast.error(
          'Medical Records workflow is not enabled for this matter',
          {
            description:
              'This matter is missing the Medical Records scope of work. ' +
              'Please escalate to #app-feedback on Slack for assistance.',
            duration: 10000,
          },
        );
      } else {
        toast.error('Failed to create requests', { description: message });
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedScopes,
    getProviderServiceIdForScope,
    matterId,
    providerId,
    notes,
    serviceStartDate,
    serviceEndDate,
    onSuccess,
    setSubmitting,
    setDestination,
    createRequest,
  ]);

  return { submit };
};
